/**
 * PaymentStatement entity class
 */

import moment from 'moment'

/**
 * PaymentStatementFactory
 * returns PaymentStatement entity class
 *
 * @class PaymentStatementFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.PaymentStatement} class
 *
 * @ngdoc factory
 * @name mwApp.factory:PaymentStatementFactory
 */
/* @ngInject */
export default function PaymentStatementFactory ($http, alertLiteSvc, Type, Entity) {
  /* === PaymentStatement class === */

  /**
   * PaymentStatement model
   *
   * @class PaymentStatement
   * @classdesc PaymentStatement model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.PaymentStatement} PaymentStatement entity
   */
  function PaymentStatement () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  PaymentStatement.prototype = Object.create(Entity.prototype)
  PaymentStatement.prototype.constructor = PaymentStatement

  /* === PaymentStatement instance methods & fields === */

  Object.assign(PaymentStatement.prototype, {

    /**
     * instance fields
     *
     * @memberof models.PaymentStatement#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      $captureId: String,
      activeFrom: moment,
      activeUntil: moment,
      autoPay: Boolean,
      category: String,
      checkNumber: String,
      comment: String,
      connectorId: String,
      creditCard: 'CreditCard',
      creditCardNumber: String,
      currency: 'Currency',
      currencyCode: String,
      discounted: Boolean,
      errorNote: String,
      externalPaymentSystemType: String, // EXTERNAL_PAYMENT_SYSTEM_TYPES
      jsonSubscriptions: Type.JSON,
      length: String,
      location: String,
      paidDevices: Number,
      paymentSystemType: String, // PAYMENT_SYSTEM_TYPES
      paymentType: String, // PAYMENT_TYPES
      periodUnlimited: Boolean,
      pricingRuleApplied: Boolean,
      processDate: moment,
      processedBy: String,
      processedTime: moment,
      prorated: Boolean,
      paymentMethod: String,
      purchaseItems: ['Purchase'],
      purchaseNumber: String,
      sourceType: String,
      status: String, // PAYMENT_STATUSES
      subscriber: 'Subscriber',
      subscriberId: Number,
      subscriptionStatus: String,
      subscriptions: ['Subscription'],
      totalPrice: Number,
      transactionId: String,
      user: String,
      transactionFeeAmount: Number
    },

    /**
     * url template
     *
     * @memberof models.PaymentStatement#
     * @type {String}
     */
    _urlTemplate: 'api/subscribers/{{::subscriberId || subscriber.id}}/payments/{{::id}}',
    _prepare: function () {
      this.$captureId = this.purchaseItems.find(item => item.captureId)?.captureId
    },

    /**
     * Get main subscription
     *
     * @memberof models.PaymentStatement#
     * @return {models.Subscription}
     */
    _mainSubscription: function () {
      if (this.subscriptions) {
        for (var i = 0; i < this.subscriptions.length; i++) {
          if (this.subscriptions[i].subscriptionType === 'Subscription') {
            return this.subscriptions[i]
          }
        }
      }
    },

    /**
     * Set comment for payment statement
     *
     * @memberof models.PaymentStatement#
     * @param {String} [text] - comment text (if absent, this.comment will be used)
     * @return {Promise} promise
     */
    _comment: function (text) {
      var comment = text || this.comment
      var original = this.comment
      return $http.patch(`api/subscribers/${this.subscriberId}/purchases/${this.id}`, { comment })
        .then(
          () => {
            this.comment = comment
          }
        ).catch(
          rejection => {
            this.comment = original
            return alertLiteSvc.alertError(rejection)
          }
        )
    },

    /**
     * Load internal comment change history for this purchase (paged, SmartTable).
     *
     * @memberof models.PaymentStatement#
     * @param {Object} tableState - smart-table state (sort, pagination)
     * @return {Promise<{content: Array}>} promise with current page rows in content
     */
    _commentHistories: function (tableState) {
      var params = {
        'sort-by': tableState.sort && tableState.sort.predicate ? tableState.sort.predicate : '',
        'sort-order': tableState.sort && tableState.sort.reverse === false ? 'asc' : 'desc',
        page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
        count: tableState.pagination.number || 25
      }
      return $http.get(`api/subscribers/${this.subscriberId}/purchases/${this.id}/comment-histories`, { params })
        .then(function (response) {
          var d = response.data
          if (d == null) {
            tableState.pagination.start = 0
            tableState.pagination.numberOfPages = 0
            tableState.pagination.totalItemCount = 0
            return { content: [] }
          }
          var payload = angular.isObject(d) && Object.prototype.hasOwnProperty.call(d, 'payload') ? d.payload : d
          if (angular.isArray(payload)) {
            tableState.pagination.start = 0
            tableState.pagination.numberOfPages = 1
            tableState.pagination.totalItemCount = payload.length
            return { content: payload }
          }
          if (!payload || !angular.isObject(payload)) {
            tableState.pagination.start = 0
            tableState.pagination.numberOfPages = 0
            tableState.pagination.totalItemCount = 0
            return { content: [] }
          }
          var content = angular.isArray(payload.content) ? payload.content : []
          if (Array.isArray(payload.sort) && payload.sort.length) {
            var last = payload.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = payload.number * payload.size
          tableState.pagination.number = payload.size
          tableState.pagination.numberOfPages = payload.totalPages
          tableState.pagination.totalItemCount = payload.totalElements
          tableState.pagination.last = payload.last
          return { content: content }
        })
        .catch(function (rejection) {
          return alertLiteSvc.alertError(rejection)
        })
    },

    /**
     * Void payment statement
     *
     * @memberof models.PaymentStatement#
     * @return {Promise} promise
     */
    _void: function () {
      var status = this.status
      return $http.delete(`api/subscribers/${this.subscriberId}/purchases/${this.id}`)
        .then(
          () => {
            this.status = 'VOIDED'
          }
        ).catch(
          rejection => {
            this.status = status
            throw rejection
          }
        )
    },

    /**
     * Approve payment statement
     *
     * @memberof models.PaymentStatement#
     * @return {Promise} promise
     */
    _approve: function () {
      var status = this.status
      return $http.patch(this._url + '/approve')
        .catch(
          rejection => {
            this.status = status
            return alertLiteSvc.alertError(rejection)
          }
        )
    }

  })

  /* === PaymentStatement static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(PaymentStatement, Entity)
  PaymentStatement._name = 'PaymentStatement'
  PaymentStatement.init()

  // permissions
  PaymentStatement.access = {
    view: 'viewSubscriptionPayment',
    create: 'createSubscriptionPayment'
  }

  return PaymentStatement
}
